import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import paymentsApiService, { toCustomerPaymentError } from '../../services/paymentsApi';
import { getStripePromise } from '../../lib/stripe';
import {
  canShowOrganizerStripePayment,
  formatPaymentStatusLabel,
  isApprovedBookingStatus,
  isDepositPaidBookingPaymentStatus,
  isPaidBookingPaymentStatus
} from '../../utils/bookingPayment';
import { resolvePaymentBreakdown } from '../../utils/bookingPaymentFees';
import { resolveBookingAmounts } from '../../utils/bookingAmounts';
import { useAuth } from '../../contexts/AuthContext';
import {
  isPaymentSessionAuthError,
  recoverPaymentSession
} from '../../utils/paymentSession';
import PaymentBreakdown from '../Common/PaymentBreakdown';
import './BookingStripePayment.css';

const StripePaymentForm = ({ bookingId, setupIntentId, returnUrl, onComplete, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements || submitting) return;

    setSubmitting(true);
    onError('');

    try {
      const { error: submitError, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
        confirmParams: {
          return_url:
            returnUrl || `${window.location.origin}/profile/bookings/${bookingId}?pay=stripe`
        }
      });

      if (submitError) {
        throw new Error(submitError.message || 'Could not save your card.');
      }

      const resolvedIntentId = setupIntent?.id || setupIntentId;
      const paymentMethodId =
        typeof setupIntent?.payment_method === 'string'
          ? setupIntent.payment_method
          : setupIntent?.payment_method?.id;

      if (!resolvedIntentId) {
        throw new Error('Stripe did not return a setup confirmation.');
      }

      const attachResult = await paymentsApiService.confirmPaymentMethodAttached(bookingId, {
        setup_intent_id: resolvedIntentId,
        payment_method_id: paymentMethodId
      });

      if (attachResult?.action_required && attachResult?.client_secret) {
        const { error: confirmError } = await stripe.confirmCardPayment(attachResult.client_secret);
        if (confirmError) {
          throw new Error(confirmError.message || 'Could not confirm your payment.');
        }
      } else if (attachResult?.charge?.status === 'failed') {
        throw new Error(attachResult?.charge?.error || 'Payment could not be completed.');
      }

      onComplete(attachResult);
    } catch (err) {
      onError(toCustomerPaymentError(err?.message) || 'Failed to save payment method.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="booking-stripe-payment__form" onSubmit={handleSubmit}>
      <PaymentElement options={{ layout: 'tabs' }} />
      <button
        type="submit"
        className="booking-stripe-payment__submit"
        disabled={!stripe || !elements || submitting}
      >
        {submitting ? 'Processing…' : 'Pay securely'}
      </button>
    </form>
  );
};

const BookingStripePayment = ({
  bookingId,
  booking = null,
  bookingStatus,
  compact = false,
  autoOpen = false,
  paymentToken = null,
  returnUrl = null,
  onStatusChange,
  onPaymentComplete = null
}) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [authError, setAuthError] = useState(false);
  const [info, setInfo] = useState('');
  const [expanded, setExpanded] = useState(Boolean(autoOpen));
  const [formRequested, setFormRequested] = useState(Boolean(autoOpen));
  const [eligibility, setEligibility] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [setupPayload, setSetupPayload] = useState(null);
  const [paymentBreakdown, setPaymentBreakdown] = useState(null);
  const autoStartedRef = useRef(false);
  const setupInFlightRef = useRef(false);
  const stripePublishableKeyRef = useRef(null);
  const skipPaymentTokenRef = useRef(false);
  const authSessionKey = `${currentUser?.uid || ''}:${localStorage.getItem('access_token') || ''}:${paymentToken || ''}`;

  const applyPaymentToken = useCallback(() => {
    if (paymentToken && !skipPaymentTokenRef.current) {
      paymentsApiService.setPaymentToken(paymentToken);
    } else {
      paymentsApiService.clearPaymentToken();
    }
  }, [paymentToken]);

  const handlePaymentFailure = (err, { allowTokenFallback = true } = {}) => {
    const rawMessage = err?.message || 'Unable to load payment options.';
    const message = toCustomerPaymentError(rawMessage);
    const paymentLinkIssue = String(rawMessage).toLowerCase().includes('payment link')
      || String(message).toLowerCase().includes('payment link');
    if (paymentToken && allowTokenFallback && !skipPaymentTokenRef.current && paymentLinkIssue) {
      skipPaymentTokenRef.current = true;
      paymentsApiService.clearPaymentToken();
      return 'retry_without_token';
    }
    const sessionIssue = !paymentToken && isPaymentSessionAuthError(message);
    setAuthError(sessionIssue);
    setError(message);
    setFormRequested(false);
    setSetupPayload(null);
    setupInFlightRef.current = false;
    return 'failed';
  };

  const paymentRowStatus = paymentStatus?.status || eligibility?.payment_status || 'not_started';

  const canStartStripeSetup =
    Boolean(eligibility?.show_pay_with_stripe) &&
    !['paid', 'scheduled', 'processing'].includes(paymentRowStatus);

  // Warm Stripe.js in the background — no UI change until the user clicks.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const config = await paymentsApiService.getStripeConfig();
        if (!cancelled && config?.publishable_key) {
          stripePublishableKeyRef.current = config.publishable_key;
          getStripePromise(config.publishable_key);
        }
      } catch {
        // setup-intent also returns publishable_key.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    skipPaymentTokenRef.current = false;
    if (paymentToken) {
      applyPaymentToken();
    }
    return () => {
      if (paymentToken) {
        paymentsApiService.clearPaymentToken();
      }
    };
  }, [paymentToken, applyPaymentToken]);

  const loadPaymentState = async () => {
    if (!bookingId) return;
    applyPaymentToken();
    setLoading(true);
    setError('');
    setAuthError(false);
    try {
      const [eligibilityResult, statusResponse] = await Promise.all([
        paymentsApiService.getBookingPaymentEligibility(bookingId).catch((err) => ({
          __error: err
        })),
        paymentsApiService.getBookingPaymentStatus(bookingId).catch(() => null)
      ]);

      if (eligibilityResult?.__error) {
        const err = eligibilityResult.__error;
        const notFound = String(err?.message || '')
          .toLowerCase()
          .includes('not found');
        if (notFound && isApprovedBookingStatus(bookingStatus)) {
          const paymentStatusKey = statusResponse?.status || 'not_started';
          setEligibility({
            show_pay_with_stripe: !['paid', 'scheduled', 'method_attached', 'processing'].includes(
              paymentStatusKey
            ),
            payment_status: paymentStatusKey,
            amount: statusResponse?.final_amount,
            charge_at: statusResponse?.charge_at
          });
        } else {
          throw err;
        }
      } else {
        setEligibility(eligibilityResult);
      }

      setPaymentStatus(statusResponse);
      const breakdownSource = {
        ...(statusResponse || {}),
        ...(eligibilityResult?.__error ? {} : eligibilityResult || {})
      };
      const bookingAmounts =
        booking && typeof booking === 'object'
          ? resolveBookingAmounts(
              {
                ...booking,
                requirements: booking.requirements,
                venue_id: booking.venueId ?? booking.venue_id,
                final_amount: booking.final_amount ?? booking.totalPrice,
                total_amount: booking.total_amount ?? booking.totalPrice
              },
              { venueId: booking.venueId ?? booking.venue_id }
            )
          : null;
      setPaymentBreakdown(
        bookingAmounts?.grandTotal > 0
          ? {
              subtotal: bookingAmounts.venueTotal,
              platformFee: bookingAmounts.platformFee,
              processingFee: bookingAmounts.processingFee,
              chargeTotal: bookingAmounts.grandTotal
            }
          : resolvePaymentBreakdown(breakdownSource, 0)
      );
      onStatusChange?.(statusResponse || eligibilityResult);
    } catch (err) {
      const outcome = handlePaymentFailure(err);
      if (outcome === 'retry_without_token') {
        await loadPaymentState();
        return;
      }
      setEligibility(null);
      setPaymentStatus(null);
      setPaymentBreakdown(null);
    } finally {
      setLoading(false);
    }
  };

  const loadPaymentStateRef = useRef(loadPaymentState);
  loadPaymentStateRef.current = loadPaymentState;

  useEffect(() => {
    autoStartedRef.current = false;
    loadPaymentStateRef.current();
  }, [bookingId, bookingStatus, booking?.totalPrice, booking?.processingFee, authSessionKey]);

  useEffect(() => {
    setupInFlightRef.current = false;
    setSetupPayload(null);
    setFormRequested(Boolean(autoOpen));
    setExpanded(Boolean(autoOpen));
    autoStartedRef.current = false;
    skipPaymentTokenRef.current = false;
  }, [bookingId, autoOpen, authSessionKey]);

  const beginSetup = async () => {
    if (!bookingId || setupPayload || setupInFlightRef.current) return;
    applyPaymentToken();
    setupInFlightRef.current = true;
    setBusy(true);
    setError('');
    setAuthError(false);
    setInfo('');
    try {
      if (isDepositPaidBookingPaymentStatus(paymentRowStatus)) {
        const chargeResult = await paymentsApiService.retryPayment(bookingId);
        if (chargeResult?.client_secret) {
          throw new Error('Additional card confirmation is required. Please try again.');
        }
        setInfo(
          chargeResult?.status === 'paid'
            ? 'Payment completed successfully.'
            : 'Payment submitted. We will update your booking shortly.'
        );
        await loadPaymentState();
        return;
      }
      const payload = await paymentsApiService.createSetupIntent(bookingId);
      if (!payload?.client_secret) {
        throw new Error('Stripe setup session missing from API.');
      }
      const publishableKey = payload.publishable_key || stripePublishableKeyRef.current;
      if (publishableKey) {
        stripePublishableKeyRef.current = publishableKey;
        getStripePromise(publishableKey);
      }
      setSetupPayload(payload);
    } catch (err) {
      const outcome = handlePaymentFailure(err);
      if (outcome === 'retry_without_token') {
        await beginSetup();
      }
    } finally {
      setupInFlightRef.current = false;
      setBusy(false);
    }
  };

  const openPaymentForm = () => {
    setExpanded(true);
    setFormRequested(true);
    beginSetup();
  };

  const openPaymentFormRef = useRef(openPaymentForm);
  openPaymentFormRef.current = openPaymentForm;

  useEffect(() => {
    if (!autoOpen || loading || autoStartedRef.current) return;
    if (canStartStripeSetup) {
      autoStartedRef.current = true;
      openPaymentFormRef.current();
    }
  }, [autoOpen, loading, canStartStripeSetup]);

  const handleSetupComplete = async (attachResult) => {
    setSetupPayload(null);
    setFormRequested(false);
    const paid = attachResult?.charge?.status === 'paid' || attachResult?.status === 'paid';
    const completedDeposit =
      paid &&
      (eligibility?.next_payment_phase === 'deposit' ||
        attachResult?.charge?.phase === 'deposit' ||
        attachResult?.phase === 'deposit');
    setInfo(
      paid
        ? completedDeposit
          ? 'Deposit received — your inquiry has been sent to the venue.'
          : 'Payment completed successfully.'
        : 'Payment submitted. We will update your booking shortly.'
    );
    await loadPaymentState();
    if (paid) {
      onPaymentComplete?.(attachResult);
    }
  };

  if (!bookingId) return null;

  if (!canShowOrganizerStripePayment(booking, bookingStatus)) {
    return null;
  }

  if (loading) {
    return compact ? null : (
      <div className="booking-stripe-payment">
        <p className="booking-stripe-payment__meta">Loading payment options…</p>
      </div>
    );
  }

  const showPayButton = Boolean(eligibility?.show_pay_with_stripe);
  const payPhase = eligibility?.next_payment_phase;
  const amountDueNow = eligibility?.amount_due_now ?? eligibility?.amount;
  const payButtonLabel =
    payPhase === 'deposit'
      ? 'Pay deposit with Stripe'
      : payPhase === 'balance'
        ? 'Pay remaining balance'
        : 'Pay with Stripe';
  const showPanel = !compact || expanded;

  if (compact && !showPayButton && paymentRowStatus !== 'paid') {
    return null;
  }

  if (compact && !expanded) {
    return (
      <button
        type="button"
        className="booking-stripe-payment__cta booking-stripe-payment__cta--compact"
        onClick={openPaymentForm}
        disabled={!showPayButton}
      >
        {payButtonLabel}
      </button>
    );
  }

  if (!showPanel) return null;

  const stripePromise = (setupPayload?.publishable_key || stripePublishableKeyRef.current)
    ? getStripePromise(setupPayload?.publishable_key || stripePublishableKeyRef.current)
    : null;

  return (
    <div className="booking-stripe-payment" id="booking-stripe-payment">
      <div className="booking-stripe-payment__header">
        <h3>Pay with Stripe</h3>
        <span className={`booking-stripe-payment__pill booking-stripe-payment__pill--${paymentRowStatus}`}>
          {formatPaymentStatusLabel(paymentRowStatus)}
        </span>
      </div>

      <p className="booking-stripe-payment__hint">
        {payPhase === 'deposit'
          ? 'Pay your deposit now so we can send your booking inquiry to the venue. The card processing fee is included when you pay the remaining balance after the venue approves.'
          : 'Pay securely with Stripe. Occasia does not store card numbers.'}
      </p>

      {paymentBreakdown ? (
        <PaymentBreakdown
          subtotal={paymentBreakdown.subtotal}
          platformFee={payPhase === 'deposit' ? 0 : paymentBreakdown.platformFee}
          processingFee={payPhase === 'deposit' ? 0 : paymentBreakdown.processingFee}
          chargeTotal={
            amountDueNow != null && amountDueNow > 0 ? Number(amountDueNow) : paymentBreakdown.chargeTotal
          }
          showSubtotalLabel="Venue total"
          totalLabel={payPhase === 'deposit' ? 'Deposit due now' : 'Total due now'}
        />
      ) : amountDueNow != null ? (
        <p className="booking-stripe-payment__meta">
          Amount due now: <strong>£{Number(amountDueNow).toFixed(2)}</strong>
        </p>
      ) : eligibility?.amount != null ? (
        <p className="booking-stripe-payment__meta">
          Amount: <strong>£{Number(eligibility.amount).toFixed(2)}</strong>
        </p>
      ) : null}

      {info ? <p className="booking-stripe-payment__info">{info}</p> : null}
      {error ? <p className="booking-stripe-payment__error">{error}</p> : null}
      {authError ? (
        <button
          type="button"
          className="booking-stripe-payment__cta"
          onClick={recoverPaymentSession}
        >
          Sign in again
        </button>
      ) : null}
      {!paymentToken && authError ? (
        <p className="booking-stripe-payment__meta">
          Or open the <strong>Pay now</strong> link from your booking email to pay without signing in.
        </p>
      ) : null}

      {isPaidBookingPaymentStatus(paymentRowStatus) ? (
        <p className="booking-stripe-payment__success">This booking is paid in full.</p>
      ) : null}
      {isDepositPaidBookingPaymentStatus(paymentRowStatus) &&
      !isPaidBookingPaymentStatus(eligibility?.booking_payment_status) ? (
        <p className="booking-stripe-payment__success">
          Deposit received. You can pay the balance after the venue approves your booking.
        </p>
      ) : null}

      {showPayButton && !formRequested ? (
        <button
          type="button"
          className="booking-stripe-payment__cta"
          onClick={openPaymentForm}
        >
          {payButtonLabel}
        </button>
      ) : null}

      {!showPayButton && eligibility?.reason && paymentRowStatus !== 'paid' ? (
        <p className="booking-stripe-payment__meta">{eligibility.reason}</p>
      ) : null}

      {formRequested && !setupPayload?.client_secret && !error && busy ? (
        <div className="booking-stripe-payment__form-shell" aria-busy="true">
          <p className="booking-stripe-payment__meta">Starting Stripe…</p>
        </div>
      ) : null}

      {setupPayload?.client_secret && stripePromise ? (
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret: setupPayload.client_secret,
            appearance: { theme: 'stripe' }
          }}
        >
          <StripePaymentForm
            bookingId={bookingId}
            setupIntentId={setupPayload.setup_intent_id}
            returnUrl={returnUrl}
            onComplete={handleSetupComplete}
            onError={setError}
          />
        </Elements>
      ) : null}

      {paymentRowStatus === 'processing' ? (
        <p className="booking-stripe-payment__success">Payment is processing…</p>
      ) : null}
    </div>
  );
};

export default BookingStripePayment;
